/**
 * One-time theme migration: neumorphic-reskin light rollout.
 *
 * The neumorphic reskin (see `main.tsx`'s `defaultTheme="light"`) made Light
 * the console's new default appearance. But `next-themes` only falls back to
 * `defaultTheme` when `localStorage` has NO stored preference yet — any
 * browser that already had a stored choice (Dark, or System resolving to
 * Dark) keeps rendering the old dark look forever, since a stored value
 * always wins over `defaultTheme`. Agents who hard-refreshed after the
 * reskin shipped still saw the old dark UI and assumed the reskin never
 * deployed.
 *
 * This is a ONE-TIME nudge, not a permanent override: every seat should see
 * the neumorphic light theme at least once as part of this rollout, but the
 * toggle keeps working afterward and whatever the agent picks next (Dark,
 * System, or back to Light) is respected and persisted exactly as before —
 * gated by `THEME_MIGRATION_FLAG_KEY`, this function is a no-op on every call
 * after the first.
 */

/** Marks that this browser has already received the one-time light nudge. */
export const THEME_MIGRATION_FLAG_KEY = 'icgchat-theme-migrated-neu';

/** next-themes' own storage key for the persisted theme choice — its
 *  default (`storageKey` defaults to `"theme"` when unset, which is how
 *  `ThemeProvider` is wired in `main.tsx`). Must match exactly or next-themes
 *  won't see the forced value. */
export const NEXT_THEMES_STORAGE_KEY = 'theme';

/**
 * Runs before `<ThemeProvider>` mounts (see `main.tsx`) so next-themes reads
 * the forced value on its very first render — never a flash of the old theme
 * followed by a swap.
 *
 * Guarded with try/catch: `localStorage` can throw in private-mode Safari and
 * similar restricted storage contexts (matching the defensive style already
 * used around storage access in `store/auth.ts`). If it throws, we skip the
 * migration for this session rather than fail app boot over a rollout nudge.
 */
export function migrateThemeToLightOnce(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (window.localStorage.getItem(THEME_MIGRATION_FLAG_KEY)) {
      return;
    }
    window.localStorage.setItem(NEXT_THEMES_STORAGE_KEY, 'light');
    window.localStorage.setItem(THEME_MIGRATION_FLAG_KEY, '1');
  } catch {
    // Storage disabled/unavailable — nothing to do; next-themes will just
    // fall back to `defaultTheme` as it always did pre-migration.
  }
}
